// Types spécifiques aux étudiants - Conformité E-DEP (Ministère de l'Enseignement Supérieur)
// Ces champs s'ajoutent aux champs communs partagés par tous les utilisateurs

import { User, UserRole } from './global.types';

// ==================== ENUMS E-DEP ====================

/** Situation de l'étudiant */
export enum SituationEtudiant {
  NOUVEAU = 'N',      // Nouveau inscrit
  REDOUBLANT = 'R',   // Redoublant
  PROMU = 'P',        // Promu (ni nouveau inscrit ni redoublant)
  DIPLOME = 'D',      // Diplômé
}

export const SituationEtudiantLabels: Record<SituationEtudiant, string> = {
  [SituationEtudiant.NOUVEAU]: 'Nouveau inscrit',
  [SituationEtudiant.REDOUBLANT]: 'Redoublant',
  [SituationEtudiant.PROMU]: 'Promu',
  [SituationEtudiant.DIPLOME]: 'Diplômé',
};

/** Mention du diplôme d'accès */
export enum MentionDiplome {
  PASSABLE = 'p',
  ASSEZ_BIEN = 'ab',
  BIEN = 'b',
  TRES_BIEN = 'tb',
  EXCELLENT = 'e',
  NON_SPECIFIE = 'ns',
}

export const MentionDiplomeLabels: Record<MentionDiplome, string> = {
  [MentionDiplome.PASSABLE]: 'Passable',
  [MentionDiplome.ASSEZ_BIEN]: 'Assez bien',
  [MentionDiplome.BIEN]: 'Bien',
  [MentionDiplome.TRES_BIEN]: 'Très bien',
  [MentionDiplome.EXCELLENT]: 'Excellent',
  [MentionDiplome.NON_SPECIFIE]: 'Non spécifié',
};

/** Résident (hébergement universitaire) */
export enum ResidentStatus {
  OUI = 'O',
  NON = 'N',
}

export const ResidentStatusLabels: Record<ResidentStatus, string> = {
  [ResidentStatus.OUI]: 'Oui - Hébergé(e) en résidence universitaire',
  [ResidentStatus.NON]: 'Non - Non hébergé(e) en résidence universitaire',
};

/** Fonctionnaire */
export enum FonctionnaireStatus {
  OUI = 'O',
  NON = 'N',
}

export const FonctionnaireStatusLabels: Record<FonctionnaireStatus, string> = {
  [FonctionnaireStatus.OUI]: 'Oui - Fonctionnaire',
  [FonctionnaireStatus.NON]: 'Non - Non fonctionnaire',
};

/** Mobilité */
export enum MobiliteStatus {
  OUI = 'O',
  NON = 'N',
}

export const MobiliteStatusLabels: Record<MobiliteStatus, string> = {
  [MobiliteStatus.OUI]: 'Oui - En mobilité',
  [MobiliteStatus.NON]: 'Non - Pas en mobilité',
};

// ==================== INTERFACES ====================

/**
 * Champs spécifiques étudiant conformes E-DEP
 * Tous ces champs sont OBLIGATOIRES pour la conformité ministérielle
 */
export interface ChampsEtudiantEDEP {
  // ===== Inscription Académique =====

  /** N° Massar/CNE - Code Massar ou CNE pour les marocains, 'be' pour les autres */
  numeroMassarCNE: string;

  /** Diplôme où l'étudiant est inscrit (depuis nomenclature filières - colonne diplôme) */
  diplomeInscrit: string;

  /** Filière correspondant à la formation (depuis nomenclature filières) */
  filiere: string;

  /** Code filière correspondant à la formation (depuis nomenclature filières) */
  codeFiliere: string;

  /** Niveau de formation (1, 2, 3, 4, 5...) */
  niveauFormation: number;

  /** Situation: N (Nouveau), R (Redoublant), P (Promu), D (Diplômé) */
  situation: SituationEtudiant;

  // ===== Diplôme d'Accès =====

  /** Diplôme d'accès ayant permis l'admission (depuis nomenclature Diplômes) */
  diplomeAcces: string;

  /** Série du baccalauréat (depuis nomenclature Série Bac) */
  serieBaccalaureat: string;

  /** Année d'obtention du baccalauréat (format AAAA/AAAA+1, ex: 2020/2021) */
  anneeObtentionBac: string;

  /** Spécialité du diplôme d'accès (texte libre: Gestion, Informatique, etc.) */
  specialiteDiplomeAcces: string;

  /** Mention du diplôme d'accès */
  mentionDiplomeAcces: MentionDiplome;

  /** Lieu d'obtention du diplôme d'accès (depuis nomenclature Provinces) */
  lieuObtentionDiplomeAcces: string;

  // ===== Statut et Inscription =====

  /** 1ère année d'inscription à l'établissement (format AAAA/AAAA+1, ex: 2021/2022) */
  premiereAnneeInscription: string;

  /** Résident: O (hébergé en résidence) ou N (non hébergé) */
  resident: ResidentStatus;

  /** Fonctionnaire: O (oui) ou N (non) */
  fonctionnaire: FonctionnaireStatus;

  /** Pourcentage de la bourse (0-100, ex: 100 pour bourse complète) */
  pourcentageBourse: number;

  /** Mobilité: O (en mobilité) ou N (pas en mobilité) */
  mobilite: MobiliteStatus;
}

/**
 * Interface complète Étudiant (étend User avec champs E-DEP)
 */
export interface Etudiant extends User, ChampsEtudiantEDEP {
  role: UserRole.STUDENT;

  // Champs additionnels système
  studentCode?: string;
  enrollmentDate?: string;
  academicYearId?: string;
}

/**
 * Données du formulaire d'inscription étudiant
 */
export interface EtudiantFormData extends ChampsEtudiantEDEP {
  // Les champs communs User seront gérés séparément
}

/**
 * Options pour les dropdowns du formulaire étudiant
 */
export interface EtudiantFormOptions {
  filieres: Array<{
    id: string;
    codeFiliere: string;
    filiere: string;
    diplome: string;
  }>;
  diplomes: Array<{
    id: string;
    labelFr: string;
  }>;
  seriesBac: Array<{
    id: string;
    code: string;
    labelFr: string;
  }>;
  provinces: Array<{
    id: string;
    nameFr: string;
  }>;
}

// ==================== VALEURS PAR DÉFAUT ====================

export const defaultEtudiantFormData: EtudiantFormData = {
  numeroMassarCNE: '',
  diplomeInscrit: '',
  filiere: '',
  codeFiliere: '',
  niveauFormation: 1,
  situation: SituationEtudiant.NOUVEAU,
  diplomeAcces: '',
  serieBaccalaureat: '',
  anneeObtentionBac: '',
  specialiteDiplomeAcces: '',
  mentionDiplomeAcces: MentionDiplome.NON_SPECIFIE,
  lieuObtentionDiplomeAcces: '',
  premiereAnneeInscription: '',
  resident: ResidentStatus.NON,
  fonctionnaire: FonctionnaireStatus.NON,
  pourcentageBourse: 0,
  mobilite: MobiliteStatus.NON,
};
